import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { paths } from "@clevis/shared-types";
import { Input, Label, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { CustomerPicker } from "./CustomerPicker";
import { ProjectItemSelect, useProjectItems } from "./ProjectItems";

// Who new jobs from a work order belong to: the builder (customer) and the
// development (project) under it, e.g. Pulte Homes → Tobiano. Clevis suggests
// both from the document; every choice can be changed, including mapping to
// any other customer or project with search-as-you-type.

export interface CustomerProjectChoice {
  customerId?: string;
  newCustomerName?: string;
  projectId?: string;
  newProjectName?: string;
  projectItemId?: string;
}

type Cust = { kind: "match"; id: string } | { kind: "create" } | { kind: "other" };
type Proj = { kind: "none" } | { kind: "existing"; id: string } | { kind: "create" };
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

export function IntakeCustomerProject({
  tenantId,
  itemId,
  matches,
  builderName,
  community,
  hints = "",
  newJobs,
  onChange,
}: {
  tenantId: string;
  itemId: string;
  matches: Array<{ id: string; displayName: string }>;
  builderName: string | null;
  community: string | null;
  /** The document's own words (title, scope, lines): where "Elevation A" would be named. */
  hints?: string;
  newJobs: number;
  onChange: (choice: CustomerProjectChoice) => void;
}) {
  const [cust, setCust] = useState<Cust>(matches[0] ? { kind: "match", id: matches[0].id } : { kind: "create" });
  const [newCustomer, setNewCustomer] = useState(builderName ?? "");
  const [other, setOther] = useState<{ id: string; name: string } | null>(null);
  const customerId = cust.kind === "match" ? cust.id : cust.kind === "other" ? (other?.id ?? "") : "";
  const customerLabel =
    cust.kind === "match"
      ? (matches.find((m) => m.id === cust.id)?.displayName ?? "")
      : cust.kind === "other"
        ? (other?.name ?? "")
        : newCustomer.trim();

  const [projects, setProjects] = useState<Array<{ id: string; name: string; status: string }> | null>(null);
  const [proj, setProj] = useState<Proj>(community ? { kind: "create" } : { kind: "none" });
  const [projTouched, setProjTouched] = useState(false);
  const [newProject, setNewProject] = useState(community ?? "");
  const [projSearch, setProjSearch] = useState("");

  useEffect(() => {
    setProjects(customerId ? null : []);
    if (!customerId) return;
    return onSnapshot(query(collection(db, paths.tenantProjects(tenantId)), where("customerId", "==", customerId)), (snap) =>
      setProjects(
        snap.docs
          .map((d) => ({ id: d.id, name: (d.get("name") as string) ?? "", status: (d.get("status") as string) ?? "active" }))
          .sort((a, b) => Number(a.status !== "active") - Number(b.status !== "active") || a.name.localeCompare(b.name)),
      ),
    );
  }, [tenantId, customerId]);

  // Suggest the development: an existing project of this customer with the
  // community's name, else a new project named after it.
  const suggested = useMemo(() => {
    if (!community || !projects) return null;
    const c = norm(community);
    return projects.find((p) => norm(p.name) === c) ?? projects.find((p) => c && (norm(p.name).includes(c) || c.includes(norm(p.name)))) ?? null;
  }, [projects, community]);
  useEffect(() => {
    if (!projects) return;
    if (!projTouched) {
      setProj(suggested ? { kind: "existing", id: suggested.id } : community ? { kind: "create" } : { kind: "none" });
    } else if (proj.kind === "existing" && !projects.some((p) => p.id === proj.id)) {
      setProj(community ? { kind: "create" } : { kind: "none" }); // that project belongs to the old customer
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, suggested]);

  // The project's bid options; suggest the one the document names.
  const items = useProjectItems(tenantId, proj.kind === "existing" ? proj.id : null);
  const [pickedItemId, setPickedItemId] = useState("");
  const [itemTouched, setItemTouched] = useState(false);
  const suggestedItem = useMemo(() => {
    const text = norm(hints);
    return items.filter((i) => i.status !== "archived").find((i) => norm(i.name).length >= 3 && text.includes(norm(i.name))) ?? null;
  }, [items, hints]);
  useEffect(() => {
    if (!itemTouched) setPickedItemId(suggestedItem?.id ?? "");
    else if (pickedItemId && !items.some((i) => i.id === pickedItemId)) setPickedItemId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedItem, items]);

  useEffect(() => {
    onChange({
      ...(cust.kind === "create" ? { newCustomerName: newCustomer.trim() || undefined } : { customerId: customerId || undefined }),
      ...(proj.kind === "existing" ? { projectId: proj.id } : proj.kind === "create" && newProject.trim() ? { newProjectName: newProject.trim() } : {}),
      ...(proj.kind === "existing" && pickedItemId ? { projectItemId: pickedItemId } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cust, customerId, newCustomer, proj, newProject, pickedItemId]);

  const pickProj = (p: Proj) => {
    setProjTouched(true);
    setProj(p);
  };
  const projectLabel =
    proj.kind === "existing" ? (projects?.find((p) => p.id === proj.id)?.name ?? "") : proj.kind === "create" ? newProject.trim() : "";
  const shownProjects = (projects ?? []).filter((p) => !projSearch.trim() || norm(p.name).includes(norm(projSearch)));
  const radio = `cp-${itemId}`;

  return (
    <div className="mt-2 space-y-3 rounded-lg bg-cream/50 p-2 text-xs">
      <p className="text-ink">
        <span className="font-semibold">Suggested: </span>
        {customerLabel ? (
          <>
            {cust.kind === "create" ? "new customer " : ""}
            <strong>{customerLabel}</strong>
          </>
        ) : (
          "no customer"
        )}
        {projectLabel ? (
          <>
            {" → "}
            {proj.kind === "create" ? "new project " : "project "}
            <strong>{projectLabel}</strong>
          </>
        ) : null}
        {proj.kind === "existing" && pickedItemId ? (
          <>
            {" → item "}
            <strong>{items.find((i) => i.id === pickedItemId)?.name}</strong>
          </>
        ) : null}
        {` → ${newJobs} new job${newJobs === 1 ? "" : "s"}`}
      </p>

      <div>
        <Label>Customer</Label>
        <div className="space-y-1">
          {matches.map((m) => (
            <label key={m.id} className="flex cursor-pointer items-center gap-1.5">
              <input type="radio" name={`${radio}-c`} checked={cust.kind === "match" && cust.id === m.id} onChange={() => setCust({ kind: "match", id: m.id })} />
              <span>
                Link to <strong>{m.displayName}</strong>
              </span>
            </label>
          ))}
          <label className="flex cursor-pointer items-center gap-1.5">
            <input type="radio" name={`${radio}-c`} checked={cust.kind === "create"} onChange={() => setCust({ kind: "create" })} />
            <span className="shrink-0">Create new:</span>
            <Input
              className="py-1 text-xs"
              value={newCustomer}
              placeholder="Customer name"
              onFocus={() => setCust({ kind: "create" })}
              onChange={(e) => setNewCustomer(e.target.value)}
            />
          </label>
          <label className="flex cursor-pointer items-center gap-1.5">
            <input type="radio" name={`${radio}-c`} checked={cust.kind === "other"} onChange={() => setCust({ kind: "other" })} />
            <span>Map to another customer…</span>
          </label>
          {cust.kind === "other" ? (
            <div className="ml-5">
              <CustomerPicker
                tenantId={tenantId}
                value={other?.id ?? ""}
                valueName={other?.name ?? ""}
                onChange={(c) => setOther(c ? { id: c.id, name: c.displayName } : null)}
              />
            </div>
          ) : null}
        </div>
      </div>

      <div>
        <Label>Project (development)</Label>
        <div className="space-y-1">
          {suggested ? (
            <label className="flex cursor-pointer items-center gap-1.5">
              <input type="radio" name={`${radio}-p`} checked={proj.kind === "existing" && proj.id === suggested.id} onChange={() => pickProj({ kind: "existing", id: suggested.id })} />
              <span>
                Add to <strong>{suggested.name}</strong> (existing)
              </span>
            </label>
          ) : null}
          <label className="flex cursor-pointer items-center gap-1.5">
            <input type="radio" name={`${radio}-p`} checked={proj.kind === "create"} onChange={() => pickProj({ kind: "create" })} />
            <span className="shrink-0">Create project:</span>
            <Input
              className="py-1 text-xs"
              value={newProject}
              placeholder="e.g. Tobiano"
              onFocus={() => pickProj({ kind: "create" })}
              onChange={(e) => setNewProject(e.target.value)}
            />
          </label>
          {(projects ?? []).length > 0 ? (
            <div>
              <Input
                className="py-1 text-xs"
                placeholder={`Search ${customerLabel || "this customer"}'s ${projects!.length} projects…`}
                value={projSearch}
                onChange={(e) => setProjSearch(e.target.value)}
              />
              {projSearch.trim() || !suggested ? (
                <div className="mt-1 max-h-40 overflow-auto rounded border border-steel/20 bg-white">
                  {shownProjects.slice(0, 40).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={cn(
                        "block w-full px-2 py-1 text-left",
                        proj.kind === "existing" && proj.id === p.id ? "bg-brandorange/10 font-semibold" : "hover:bg-cream",
                      )}
                      onClick={() => pickProj({ kind: "existing", id: p.id })}
                    >
                      {p.name}
                      {p.status !== "active" ? <span className="text-steel"> (closed)</span> : null}
                    </button>
                  ))}
                  {shownProjects.length === 0 ? <div className="px-2 py-1 text-steel">No project matches.</div> : null}
                </div>
              ) : null}
            </div>
          ) : null}
          <label className="flex cursor-pointer items-center gap-1.5">
            <input type="radio" name={`${radio}-p`} checked={proj.kind === "none"} onChange={() => pickProj({ kind: "none" })} />
            <span>No project</span>
          </label>
        </div>
      </div>

      {proj.kind === "existing" && items.length > 0 ? (
        <div>
          <Label>Item (bid option){suggestedItem && !itemTouched ? <span className="font-normal text-steel"> · found "{suggestedItem.name}" in the email</span> : null}</Label>
          <ProjectItemSelect
            className="py-1 text-xs"
            items={items}
            value={pickedItemId}
            onChange={(it) => {
              setItemTouched(true);
              setPickedItemId(it?.id ?? "");
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
